/** Refresh context (UX review F3) — makes the LiveIndicator real.
 *
 * DashboardLayout provides this context; the LiveIndicator toggles
 * `autoRefresh` and bumps `refreshKey` on manual refresh. Pages consume
 * it via `useAutoRefresh()` so the shell control actually drives data.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

export interface RefreshContextValue {
  /** Whether interval polling is enabled (LiveIndicator switch). */
  autoRefresh: boolean;
  toggleAutoRefresh: () => void;
  /** Increments whenever the user presses manual refresh. */
  refreshKey: number;
  triggerRefresh: () => void;
  /** True while a manual refresh is in flight (spinner in the toolbar). */
  refreshing: boolean;
  setRefreshing: (v: boolean) => void;
}

const RefreshContext = createContext<RefreshContextValue>({
  autoRefresh: true,
  toggleAutoRefresh: () => {},
  refreshKey: 0,
  triggerRefresh: () => {},
  refreshing: false,
  setRefreshing: () => {},
});

/** Provider for the refresh context. */
export function RefreshProvider({ children }: { children: React.ReactNode }) {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const refreshTimer = useRef<number | null>(null);

  const toggleAutoRefresh = useCallback(() => {
    setAutoRefresh((prev) => !prev);
  }, []);

  const triggerRefresh = useCallback(() => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
    // Safety: clear the spinner if consumers don't (hook clears it on settle).
    if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
    refreshTimer.current = window.setTimeout(() => setRefreshing(false), 5000);
  }, []);

  const value = useMemo(
    () => ({
      autoRefresh,
      toggleAutoRefresh,
      refreshKey,
      triggerRefresh,
      refreshing,
      setRefreshing,
    }),
    [autoRefresh, toggleAutoRefresh, refreshKey, triggerRefresh, refreshing]
  );

  return <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>;
}

/** Access the refresh context (works standalone with defaults). */
export function useRefresh(): RefreshContextValue {
  return useContext(RefreshContext);
}
